// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/events/broadcastBus.ts
var BroadcastBus = class {
  constructor(name, validate) {
    this.name = name;
    this.validate = validate;
  }
  name;
  validate;
  static {
    brandClass(this, "events/BroadcastBus");
  }
  channel = null;
  handlers = /* @__PURE__ */ new Set();
  start() {
    if (this.channel || typeof BroadcastChannel === "undefined") return;
    try {
      this.channel = new BroadcastChannel(this.name);
      this.channel.onmessage = ({ data }) => {
        if (!this.validate(data)) return;
        for (const handler of this.handlers) {
          try {
            handler(data);
          } catch {
          }
        }
      };
    } catch {
    }
  }
  publish(message) {
    try {
      this.channel?.postMessage(message);
    } catch {
    }
  }
  subscribe(handler) {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }
  stop() {
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel.close();
      this.channel = null;
    }
    this.handlers.clear();
  }
};

// src/events/eventDeduper.ts
var EventDeduper = class {
  constructor(capacity = 2e3) {
    this.capacity = capacity;
  }
  capacity;
  static {
    brandClass(this, "events/EventDeduper");
  }
  /** Insertion-ordered map doubles as the LRU recency queue. */
  seen = /* @__PURE__ */ new Set();
  /**
   * Record `key` and report whether it's new. A null key is never de-duplicated
   * (caller couldn't derive an id) — treated as new so nothing is dropped.
   */
  accept(key) {
    if (key == null) return true;
    if (this.seen.has(key)) {
      this.seen.delete(key);
      this.seen.add(key);
      return false;
    }
    this.seen.add(key);
    if (this.seen.size > this.capacity) {
      const oldest = this.seen.values().next().value;
      if (oldest !== void 0) this.seen.delete(oldest);
    }
    return true;
  }
  has(key) {
    return this.seen.has(key);
  }
  clear() {
    this.seen.clear();
  }
  get size() {
    return this.seen.size;
  }
};

// src/ids/index.ts
function webCrypto() {
  return globalThis.crypto;
}
function randomBytes(count) {
  const bytes = new Uint8Array(count);
  const crypto = webCrypto();
  if (typeof crypto?.getRandomValues === "function") crypto.getRandomValues(bytes);
  else for (let i = 0; i < count; i++) bytes[i] = Math.floor(Math.random() * 256);
  return bytes;
}
function hexOf(bytes) {
  let hex = "";
  for (const byte of bytes) hex += byte.toString(16).padStart(2, "0");
  return hex;
}
function randomUuid() {
  const crypto = webCrypto();
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  const bytes = randomBytes(16);
  bytes[6] = bytes[6] & 15 | 64;
  bytes[8] = bytes[8] & 63 | 128;
  const hex = hexOf(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// src/events/resourceChanges.ts
var operations = /* @__PURE__ */ new Set(["created", "updated", "deleted", "invalidated"]);
function isChange(value) {
  if (!value || typeof value !== "object") return false;
  const v = value;
  return v.version === 1 && typeof v.scope === "string" && typeof v.eventId === "string" && v.eventId.length > 0 && v.eventId.length <= 2048 && typeof v.resource === "string" && operations.has(v.operation) && (v.id === void 0 || typeof v.id === "string" && v.id.length > 0 && v.id.length <= 2048);
}
var eventIds = globalSingleton("events/resource-change-ids", () => ({
  sender: randomUuid(),
  sequence: 0
}));
var ResourceChangeHub = class {
  constructor(scope, options) {
    this.scope = scope;
    this.options = options;
    if (!scope) throw new Error("Resource changes require an authenticated scope");
    this.resources = new Set(options.resources);
    this.debounceMs = Number.isFinite(options.debounceMs) ? Math.max(0, Math.min(5e3, options.debounceMs)) : 150;
    this.bus = new BroadcastBus(
      JSON.stringify([options.namespace ?? "authe.resource-changes.v1", scope]),
      isChange
    );
  }
  scope;
  options;
  static {
    brandClass(this, "events/ResourceChangeHub");
  }
  resources;
  debounceMs;
  bus;
  seen = new EventDeduper();
  listeners = /* @__PURE__ */ new Set();
  cancellations = /* @__PURE__ */ new Set();
  active = false;
  stopped = false;
  start() {
    if (this.active || this.stopped) return;
    this.active = true;
    if (this.options.crossTab !== false) {
      this.bus.subscribe((change) => this.deliver(change));
      this.bus.start();
    }
  }
  publish(input) {
    const change = this.envelope(input);
    if (this.deliver(change)) this.bus.publish(change);
  }
  /** Consume a server event locally; the streaming transport owns its fan-out. */
  ingest(input, eventId) {
    this.deliver(this.envelope(input, eventId));
  }
  /** Recover missed notifications on visibility/online/reconnect. Local only. */
  revalidate() {
    for (const resource of this.resources) this.ingest({ resource, operation: "invalidated" });
  }
  /** Immediate listeners should evict request caches before batched readers run. */
  subscribe(listener) {
    if (this.stopped) return () => {
    };
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /** Bounded by registered resources, irrespective of event volume. */
  subscribeBatched(listener) {
    if (this.stopped) return () => {
    };
    let pending = /* @__PURE__ */ new Set();
    let timer;
    const off = this.subscribe(({ resource }) => {
      pending.add(resource);
      if (timer !== void 0) return;
      timer = setTimeout(() => {
        timer = void 0;
        const batch = pending;
        pending = /* @__PURE__ */ new Set();
        if (this.active) {
          try {
            listener(batch);
          } catch {
          }
        }
      }, this.debounceMs);
    });
    const cancel = () => {
      off();
      clearTimeout(timer);
      pending.clear();
      this.cancellations.delete(cancel);
    };
    this.cancellations.add(cancel);
    return cancel;
  }
  stop() {
    this.active = false;
    this.stopped = true;
    this.bus.stop();
    for (const cancel of this.cancellations) cancel();
    this.listeners.clear();
    this.seen.clear();
  }
  envelope(input, eventId) {
    return {
      version: 1,
      scope: this.scope,
      eventId: eventId ?? `${eventIds.sender}:${++eventIds.sequence}`,
      resource: input.resource,
      operation: input.operation,
      id: input.id
    };
  }
  deliver(change) {
    if (!this.active || !isChange(change) || change.scope !== this.scope || !this.resources.has(change.resource) || !this.seen.accept(JSON.stringify([change.resource, change.eventId]))) return false;
    for (const listener of this.listeners) {
      try {
        listener(change);
      } catch {
      }
    }
    return true;
  }
};
function withResourceChange(mutation, hub, describe) {
  return async (...args) => {
    const result = await mutation(...args);
    try {
      hub.publish(describe(result, ...args));
    } catch {
    }
    return result;
  };
}

// src/events/resourceMutations.ts
function withResourceMutations(api, hub, descriptions) {
  if (!hub) return api;
  const wrapped = { ...api };
  for (const key of Object.keys(descriptions)) {
    const mutation = api[key];
    const describe = descriptions[key];
    if (typeof mutation !== "function" || !describe) continue;
    wrapped[key] = withResourceChange(
      mutation.bind(api),
      hub,
      describe
    );
  }
  return wrapped;
}

// src/events/index.ts
var EventCriticality = {
  CRITICAL: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
  SUCCESS: 5
};
var EVENT_CRITICALITY_ORDER = [
  EventCriticality.CRITICAL,
  EventCriticality.HIGH,
  EventCriticality.MEDIUM,
  EventCriticality.LOW,
  EventCriticality.SUCCESS
];
var EVENT_CRITICALITY_LABELS = {
  [EventCriticality.CRITICAL]: "Critical",
  [EventCriticality.HIGH]: "High",
  [EventCriticality.MEDIUM]: "Medium",
  [EventCriticality.LOW]: "Low",
  [EventCriticality.SUCCESS]: "Success"
};
function criticalityLabel(level) {
  return EVENT_CRITICALITY_LABELS[level] ?? `Level ${level}`;
}
var CRITICALITY_BY_NAME = {
  critical: EventCriticality.CRITICAL,
  fatal: EventCriticality.CRITICAL,
  emergency: EventCriticality.CRITICAL,
  high: EventCriticality.HIGH,
  error: EventCriticality.HIGH,
  warn: EventCriticality.MEDIUM,
  warning: EventCriticality.MEDIUM,
  medium: EventCriticality.MEDIUM,
  low: EventCriticality.LOW,
  info: EventCriticality.LOW,
  notice: EventCriticality.LOW,
  success: EventCriticality.SUCCESS,
  ok: EventCriticality.SUCCESS,
  resolved: EventCriticality.SUCCESS
};
function criticalityFromName(value, fallback = EventCriticality.MEDIUM) {
  if (value == null) return fallback;
  if (typeof value === "number") {
    return EVENT_CRITICALITY_ORDER.includes(value) ? value : fallback;
  }
  const trimmed = value.trim();
  if (trimmed !== "" && Number.isFinite(Number(trimmed))) {
    const n = Number(trimmed);
    if (EVENT_CRITICALITY_ORDER.includes(n)) {
      return n;
    }
  }
  return CRITICALITY_BY_NAME[trimmed.toLowerCase()] ?? fallback;
}
function normalizeEvent(event) {
  const level = criticalityFromName(event.criticality_level);
  if (event.criticality_level === level) return event;
  return { ...event, criticality_level: level };
}
var EventResourceType = {
  ADDRESS: "address",
  TRANSACTION: "transaction",
  LABEL: "label",
  TAG: "tag",
  GROUP: "group",
  ENTITY: "entity",
  ABI: "abi",
  PROXY: "proxy",
  STREAM: "stream",
  USER: "user",
  ORGANIZATION: "organization",
  TEAM: "team",
  INVESTIGATION: "investigation",
  PIPELINE: "pipeline",
  TRACER: "tracer",
  /** Alerting rules, watches and their delivery destinations. */
  TRIGGER: "trigger",
  /** Settings that change how data is INTERPRETED: decoding overrides,
   *  confidence policy, evidence policy, grouping rules. */
  CONFIGURATION: "configuration",
  /** What the customer PAYS FOR — the plan on their subscription, and its
   *  status. Changing it changes quotas, entitlements and the bill, and it is
   *  usually changed by somebody who is not the customer. */
  SUBSCRIPTION: "subscription",
  /** Inbound contact / lead events from `aws-connect-mgmt`. */
  APPLICATION: "application",
  AUTHENTICATION: "authentication",
  ACTIVITY_LOG: "activity_log"
};
var EVENT_RESOURCE_LABELS = {
  [EventResourceType.ADDRESS]: "Address",
  [EventResourceType.TRANSACTION]: "Transaction",
  [EventResourceType.LABEL]: "Label",
  [EventResourceType.TAG]: "Tag",
  [EventResourceType.GROUP]: "Group",
  [EventResourceType.ENTITY]: "Entity",
  [EventResourceType.ABI]: "ABI",
  [EventResourceType.PROXY]: "Proxy",
  [EventResourceType.STREAM]: "Stream",
  [EventResourceType.USER]: "User",
  [EventResourceType.ORGANIZATION]: "Organization",
  [EventResourceType.TEAM]: "Team",
  [EventResourceType.INVESTIGATION]: "Investigation",
  [EventResourceType.PIPELINE]: "Pipeline",
  [EventResourceType.TRACER]: "Tracer",
  [EventResourceType.TRIGGER]: "Trigger",
  [EventResourceType.CONFIGURATION]: "Configuration",
  [EventResourceType.SUBSCRIPTION]: "Subscription",
  [EventResourceType.APPLICATION]: "Application",
  [EventResourceType.AUTHENTICATION]: "Authentication",
  [EventResourceType.ACTIVITY_LOG]: "Activity log"
};
var EVENT_RESOURCE_ORDER = [
  EventResourceType.ADDRESS,
  EventResourceType.TRANSACTION,
  EventResourceType.ABI,
  EventResourceType.LABEL,
  EventResourceType.TAG,
  EventResourceType.ENTITY,
  EventResourceType.GROUP,
  EventResourceType.PROXY,
  EventResourceType.INVESTIGATION,
  EventResourceType.PIPELINE,
  EventResourceType.TRACER,
  EventResourceType.TRIGGER,
  EventResourceType.CONFIGURATION,
  EventResourceType.SUBSCRIPTION,
  EventResourceType.STREAM,
  EventResourceType.USER,
  EventResourceType.TEAM,
  EventResourceType.ORGANIZATION,
  EventResourceType.AUTHENTICATION,
  EventResourceType.APPLICATION,
  EventResourceType.ACTIVITY_LOG
];
var UNPUBLISHED_EVENT_RESOURCES = /* @__PURE__ */ new Set([
  EventResourceType.STREAM,
  EventResourceType.APPLICATION,
  EventResourceType.ACTIVITY_LOG
]);
var EVENT_RESOURCE_FILTERS = EVENT_RESOURCE_ORDER.filter(
  (resource) => !UNPUBLISHED_EVENT_RESOURCES.has(resource)
);
function resourceLabel(resource) {
  return EVENT_RESOURCE_LABELS[resource] ?? resource;
}
var EventChainType = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  OTHER: "other"
};
function eventActorId(event) {
  if (typeof event.created_by === "string" && event.created_by) return event.created_by;
  const meta = event.metadata ?? {};
  const candidate = meta["actor_user_id"] ?? meta["audience_user_id"];
  return typeof candidate === "string" && candidate ? candidate : null;
}
function formatPrincipal(id) {
  if (!id) return "Someone";
  const tail = id.includes("|") ? id.slice(id.lastIndexOf("|") + 1) : id;
  if (/^\d{8,}$/.test(tail)) return `User \u2026${tail.slice(-4)}`;
  return tail;
}
function eventScopeLabel(event, ctx) {
  const orgId = event.org_id ?? event.org ?? null;
  const tenantId = event.tenant_id ?? null;
  const org = orgId ? ctx.resolveOrgLabel?.(orgId) ?? orgId : null;
  const tenant = tenantId ? ctx.resolveTenantLabel?.(tenantId) ?? tenantId : null;
  const parts = [org, tenant].filter((p) => Boolean(p) && p !== "unknown");
  return parts.length ? parts.join(" \xB7 ") : null;
}
function isAuthentication(event) {
  return event.resource === EventResourceType.AUTHENTICATION;
}
function isGroup(event) {
  return event.resource === EventResourceType.GROUP;
}
function groupEventTargetId(event) {
  if (!isGroup(event)) return null;
  const id = event.group_id || event.link_id;
  return typeof id === "string" && id ? id : null;
}
function eventTarget(event) {
  const id = (value) => typeof value === "string" && value.trim() ? value.trim() : null;
  const linked = id(event.link_id);
  switch (event.resource) {
    case EventResourceType.GROUP:
      return pointAt("group", id(event.group_id) ?? linked);
    case EventResourceType.TRACER:
      return pointAt("tracer", id(event.tracer_id) ?? linked);
    case EventResourceType.TRIGGER:
      return pointAt("watch", id(event.trigger_id) ?? linked);
    case EventResourceType.ADDRESS:
      return pointAt("address", id(event.address));
    default:
      return null;
  }
}
function pointAt(kind, id) {
  return id ? { kind, id } : null;
}
function describeEvent(event, ctx = {}) {
  const actorId = eventActorId(event);
  const isCurrentUser = Boolean(ctx.currentUserId && actorId === ctx.currentUserId);
  const resolvedName = actorId ? ctx.resolveActorName?.(actorId) ?? null : null;
  const actorDisplay = isCurrentUser ? "You" : resolvedName ?? formatPrincipal(actorId);
  const scope = eventScopeLabel(event, ctx);
  if (isAuthentication(event)) {
    const kind = (event.authentication_type ?? "").toLowerCase();
    const denied = event.metadata?.["login_allowed"] === false;
    let title2;
    if (denied) {
      title2 = isCurrentUser ? "Your sign-in was denied" : `${actorDisplay} was denied sign-in`;
    } else if (kind === "logout") {
      title2 = isCurrentUser ? "You signed out" : `${actorDisplay} signed out`;
    } else if (isCurrentUser && resolvedName) {
      title2 = `Signed in as ${resolvedName}`;
    } else if (isCurrentUser) {
      title2 = "You signed in";
    } else {
      title2 = `${actorDisplay} signed in`;
    }
    return {
      title: title2,
      description: scope ? `Workspace ${scope}` : void 0,
      actorId,
      isCurrentUser
    };
  }
  if (isGroup(event)) {
    const kind = (event.lifecycle_kind ?? "").toLowerCase();
    const state = (event.state ?? "").toLowerCase();
    let fallback = event.message_summary;
    if (!fallback) {
      if (kind === "group.leg_arrived") {
        fallback = state === "completed" ? "Cross-chain group complete" : "Next leg arrived";
      } else if (kind === "group.timed_out") {
        fallback = "Cross-chain group timed out";
      } else {
        fallback = resourceLabel(event.resource);
      }
    }
    return {
      title: fallback,
      description: event.message || (scope ? `Workspace ${scope}` : void 0),
      actorId,
      isCurrentUser
    };
  }
  const base = event.message_summary || resourceLabel(event.resource);
  const title = isCurrentUser && !/\byou\b/i.test(base) ? `${base} \xB7 by you` : base;
  return {
    title,
    description: event.message || (scope ? `Workspace ${scope}` : void 0),
    actorId,
    isCurrentUser
  };
}

export { BroadcastBus, CRITICALITY_BY_NAME, EVENT_CRITICALITY_LABELS, EVENT_CRITICALITY_ORDER, EVENT_RESOURCE_FILTERS, EVENT_RESOURCE_LABELS, EVENT_RESOURCE_ORDER, EventChainType, EventCriticality, EventDeduper, EventResourceType, ResourceChangeHub, UNPUBLISHED_EVENT_RESOURCES, criticalityFromName, criticalityLabel, describeEvent, eventActorId, eventTarget, formatPrincipal, groupEventTargetId, normalizeEvent, resourceLabel, withResourceChange, withResourceMutations };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map